import React, { useEffect, useState, useMemo } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Chip, Grid, 
    Avatar, Fade, Divider, CircularProgress, Container,
    Stack, Tab, Tabs, useTheme
} from '@mui/material';
import { 
    CheckCircle as PresentIcon, 
    Cancel as AbsentIcon, 
    AccessTime as LateIcon,
    CalendarMonth as CalendarIcon,
    History as HistoryIcon,
    TrendingUp as TrendIcon,
    EventAvailable as HeaderIcon,
    ArrowForwardIos as ArrowIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import { formatDatePart, getMonthNameFromDate } from '../../utils/customDate';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 40px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -40px;
    position: relative;
    z-index: 2;
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
`;

const StatCard = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const MonthTab = styled(Tab)`
    text-transform: none !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    min-width: 120px !important;
    border-radius: 12px !important;
    margin: 0 4px !important;
    transition: all 0.2s ease !important;
    color: #64748b !important;
    
    &.Mui-selected {
        color: #6366f1 !important;
        background: rgba(99, 102, 241, 0.08) !important;
    }
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const ViewStdAttendance = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { userDetails, currentUser, loading } = useSelector((state) => state.user);

    const [selectedMonthKey, setSelectedMonthKey] = useState("");

    useEffect(() => {
        if (currentUser?._id) {
            dispatch(getUserDetails(currentUser._id, "Student"));
        }
    }, [dispatch, currentUser?._id]);

    // Grouping logic
    const groupedAttendance = useMemo(() => {
        if (!userDetails?.attendance) return {};
        
        const groups = {};
        userDetails.attendance.forEach(record => {
            const cleanDate = formatDatePart(record.date);
            if (!cleanDate) return;

            const parts = cleanDate.split('-');
            const year = parts[0];
            const monthNum = parts[1];
            const key = `${year}-${monthNum}`;

            if (!groups[key]) {
                const monthName = getMonthNameFromDate(cleanDate);
                groups[key] = {
                    monthName,
                    year,
                    records: [],
                    stats: { present: 0, absent: 0, late: 0, total: 0 }
                };
            }
            
            groups[key].records.push(record);
            groups[key].stats.total++;
            if (record.status === 'Present') groups[key].stats.present++;
            else if (record.status === 'Absent') groups[key].stats.absent++;
            else if (record.status === 'Late') {
                groups[key].stats.late++;
                groups[key].stats.present++;
            }
        });

        Object.keys(groups).forEach(key => {
            groups[key].records.sort((a, b) => formatDatePart(b.date).localeCompare(formatDatePart(a.date)));
            groups[key].percentage = (groups[key].stats.present / groups[key].stats.total) * 100;
        });

        return groups;
    }, [userDetails]);

    // Available months sorted by date
    const sortedMonthKeys = useMemo(() => {
        return Object.keys(groupedAttendance).sort((a, b) => {
            const [y1, m1] = a.split('-').map(Number);
            const [y2, m2] = b.split('-').map(Number);
            return y2 - y1 || m2 - m1;
        });
    }, [groupedAttendance]);

    // Set initial selected month
    useEffect(() => {
        if (sortedMonthKeys.length > 0 && !selectedMonthKey) {
            setSelectedMonthKey(sortedMonthKeys[0]);
        }
    }, [sortedMonthKeys, selectedMonthKey]);

    const activeMonthData = groupedAttendance[selectedMonthKey];

    const overallStats = useMemo(() => {
        if (!userDetails?.attendance) return { percentage: 0 };
        const total = userDetails.attendance.length;
        const present = userDetails.attendance.filter(a => a.status === 'Present' || a.status === 'Late').length;
        return {
            total,
            present,
            percentage: total > 0 ? (present / total) * 100 : 0
        };
    }, [userDetails]);

    const getStatusChip = (status) => {
        switch (status) {
            case 'Present':
                return <Chip icon={<PresentIcon sx={{ fontSize: '18px !important' }} />} label="Present" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 800, borderRadius: '8px' }} />;
            case 'Absent':
                return <Chip icon={<AbsentIcon sx={{ fontSize: '18px !important' }} />} label="Absent" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 800, borderRadius: '8px' }} />;
            case 'Late':
                return <Chip icon={<LateIcon sx={{ fontSize: '18px !important' }} />} label="Late" size="small" sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 800, borderRadius: '8px' }} />;
            default:
                return <Chip label={status} size="small" variant="outlined" />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <HeaderIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        My Attendance
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        View your daily presence across months.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Academic Year Average</Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <TrendIcon sx={{ color: overallStats.percentage >= 80 ? '#10b981' : '#f59e0b' }} />
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: overallStats.percentage >= 80 ? '#10b981' : overallStats.percentage >= 60 ? '#f59e0b' : '#ef4444' }}>
                                        {overallStats.percentage.toFixed(1)}%
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" style={{maxWidth:"100%", marginTop:"3px"}}>
                {/* Month Navigation */}
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                        flexGrow: 1, 
                        bgcolor: 'white', 
                        p: 1, 
                        borderRadius: '16px', 
                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                        overflow: 'hidden'
                    }}>
                        <Tabs 
                            value={selectedMonthKey} 
                            onChange={(e, v) => setSelectedMonthKey(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTabs-indicator': { display: 'none' }
                            }}
                        >
                            {sortedMonthKeys.map(key => (
                                <MonthTab 
                                    key={key} 
                                    label={`${groupedAttendance[key].monthName} ${groupedAttendance[key].year}`} 
                                    value={key} 
                                />
                            ))}
                        </Tabs>
                    </Box>
                </Box>

                {activeMonthData ? (
                    <Grid container spacing={3}>
                        {/* Monthly Stats */}
                        <Grid item xs={12} md={3}>
                            <Stack spacing={2}>
                                <StatCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981', width: 40, height: 40 }}><PresentIcon fontSize="small" /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PRESENT</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900 }}>{activeMonthData.stats.present} Days</Typography>
                                        </Box>
                                    </Box>
                                </StatCard>
                                <StatCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#fffbeb', color: '#d97706', width: 40, height: 40 }}><LateIcon fontSize="small" /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>LATE</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900 }}>{activeMonthData.stats.late} Days</Typography>
                                        </Box>
                                    </Box>
                                </StatCard>
                                <StatCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444', width: 40, height: 40 }}><AbsentIcon fontSize="small" /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>ABSENT</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 900 }}>{activeMonthData.stats.absent} Days</Typography>
                                        </Box>
                                    </Box>
                                </StatCard>
                                <StatCard elevation={0} sx={{ bgcolor: '#6366f1 !important', color: 'white' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8 }}>MONTHLY RATIO</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900 }}>{activeMonthData.percentage.toFixed(1)}%</Typography>
                                </StatCard>
                            </Stack>
                        </Grid>

                        {/* Attendance Table */}
                        <Grid item xs={12} md={9}>
                            <GlassCard>
                                <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                        {activeMonthData.monthName} {activeMonthData.year} History
                                    </Typography>
                                    <Chip label={`${activeMonthData.stats.total} Sessions`} size="small" sx={{ fontWeight: 700 }} />
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>Date</StyledTableCell>
                                                <StyledTableCell>Weekday</StyledTableCell>
                                                <StyledTableCell align="center">Status</StyledTableCell>
                                                <StyledTableCell>Log Type</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {activeMonthData.records.map((record, index) => {
                                                const cleanDate = formatDatePart(record.date);
                                                return (
                                                    <TableRow key={index} hover sx={{ '&:last-child td': { border: 0 } }}>
                                                        <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <CalendarIcon fontSize="small" sx={{ color: '#64748b' }} />
                                                                {cleanDate}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#64748b', fontWeight: 500 }}>
                                                            {record.status === 'Late' ? 'Late Arrival' : 'Standard Session'}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {getStatusChip(record.status)}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                                            {record.subName?.subName || 'Daily General Roll Call'}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </GlassCard>
                        </Grid>
                    </Grid>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 10 }}>
                        <Avatar sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                            <HistoryIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Typography variant="h6" color="textSecondary">No attendance records found.</Typography>
                        <Typography variant="body2" color="textSecondary">Attendance data will appear here once recorded by the school.</Typography>
                    </Box>
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default ViewStdAttendance;
